/**
 * Вопрос опроса по экологии (ecology-survey, макет клиента 16.09): текст, подсказка и поле ответа по
 * типу — «Да / Нет» и выбор варианта кнопками (повторный клик снимает выбор), несколько вариантов —
 * галочками, текст и число — полем ввода. При ответе «Да» ниже — уточнения (дата, число, текст или
 * выбор варианта); у инвентаризации их два — дата разработки и число источников (анкета xlsx).
 */
import { useId } from 'react'
import type { EcoAnswer, EcoClarification, EcoQuestion } from '@/lib/api'
import { ECO_YES, ecoAnswerValues, emptyEcoAnswer } from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'

const SELECTED_OPTION = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

/** Только цифры: число вводится без пробелов и знаков (сервер принимает целое число). */
function digits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 9)
}

function Pills({
  options,
  value,
  label,
  onChange,
}: {
  options: string[]
  value: string
  label: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value === o
        return (
          <Button
            key={o}
            type="button"
            size="sm"
            variant="secondary"
            aria-pressed={on}
            className={cn('min-w-16', on && SELECTED_OPTION)}
            onClick={() => onChange(on ? '' : o)}
          >
            {o}
          </Button>
        )
      })}
    </div>
  )
}

function ClarificationField({
  clarification,
  value,
  onChange,
}: {
  clarification: EcoClarification
  value: string
  onChange: (value: string) => void
}) {
  const id = useId()
  const label = (
    <label htmlFor={id} className="mb-1.5 block text-13 text-txt-secondary">
      {clarification.label}
    </label>
  )
  switch (clarification.type) {
    case 'single':
      return (
        <div>
          <p className="mb-1.5 text-13 text-txt-secondary">{clarification.label}</p>
          <Pills
            options={clarification.options}
            value={value}
            label={clarification.label}
            onChange={onChange}
          />
        </div>
      )
    case 'date':
      return (
        <div className="max-w-48">
          {label}
          <DateInput id={id} value={value} onChange={onChange} />
        </div>
      )
    case 'number':
      return (
        <div className="max-w-48">
          {label}
          <Input
            id={id}
            inputMode="numeric"
            value={value}
            placeholder="число"
            onChange={(e) => onChange(digits(e.target.value))}
          />
        </div>
      )
    default:
      return (
        <div>
          {label}
          <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
        </div>
      )
  }
}

export function QuestionField({
  question,
  answer,
  onChange,
}: {
  question: EcoQuestion
  answer: EcoAnswer | undefined
  onChange: (answer: EcoAnswer) => void
}) {
  const inputId = useId()
  const a = answer ?? emptyEcoAnswer()
  const set = (next: Partial<EcoAnswer>) => onChange({ ...a, ...next })

  return (
    <div className="border-b border-line py-4 first:pt-0 last:border-b-0 last:pb-0">
      <label
        htmlFor={
          question.answerType === 'text' || question.answerType === 'number' ? inputId : undefined
        }
        className="block font-medium text-txt"
      >
        {question.text}
      </label>
      {question.hint ? <p className="mt-0.5 text-13 text-txt-tertiary">{question.hint}</p> : null}

      <div className="mt-2.5">
        {question.answerType === 'yes_no' || question.answerType === 'single' ? (
          <Pills
            options={ecoAnswerValues(question)}
            value={a.value}
            label={question.text}
            onChange={(value) => set({ value })}
          />
        ) : null}

        {question.answerType === 'multi' ? (
          <div className="flex flex-col gap-2.5" role="group" aria-label={question.text}>
            {question.options.map((o) => (
              <LabeledCheckbox
                key={o}
                checked={a.values.includes(o)}
                onCheckedChange={(on) =>
                  set({
                    values: question.options.filter((x) => (x === o ? on : a.values.includes(x))),
                  })
                }
              >
                {o}
              </LabeledCheckbox>
            ))}
          </div>
        ) : null}

        {question.answerType === 'text' ? (
          <Input id={inputId} value={a.value} onChange={(e) => set({ value: e.target.value })} />
        ) : null}

        {question.answerType === 'number' ? (
          <Input
            id={inputId}
            className="max-w-48"
            inputMode="numeric"
            value={a.value}
            placeholder="число"
            onChange={(e) => set({ value: digits(e.target.value) })}
          />
        ) : null}
      </div>

      {question.answerType === 'yes_no' &&
      a.value === ECO_YES &&
      question.clarifications.length > 0 ? (
        <div className="mt-3 flex flex-col gap-3 rounded-8 bg-subtle p-3 nw:flex-row nw:flex-wrap nw:items-start">
          {question.clarifications.map((c) => (
            <ClarificationField
              key={c.key}
              clarification={c}
              value={a.clarifications[c.key] ?? ''}
              onChange={(v) => set({ clarifications: { ...a.clarifications, [c.key]: v } })}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
